// lib
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import copyToClipboard from '@aytm/lib/common/copy-to-clipboard'

import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { stopEvent } from '@aytm/lib/common/func'
import { sanitizeHTML } from '@aytm/lib/common/html'

import containerExpander from '@aytm/lib/layout/container-expander'

// components
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'

import Popup, { PopupCloseButton } from '@aytm/lib/rfd-ui/components/basic/popup'

import TransitionPanel from '@aytm/app/stats/components/transition-panel/TransitionPanel'

import { can, CAPABILITY } from '@aytm/app/stats/page/data-quality-report/lib/tiers'

// styles
import style from '@aytm/app/stats/page/data-centrifuge/styles/Responses.module.scss'

const REJECTED_OPTIONS = [
  { value: 'Flagged by DC', text: 'Flagged by DC' },
  { value: 'Soft Reject', text: 'Soft Reject' },
  { value: 'other', text: 'Other' }
]

// A vector explanation is server-built markup (the group rollup joins children with <br />), so it
// cannot be escaped wholesale — but it quotes the respondent's own open-ended answer, which reaches
// this innerHTML sink verbatim. Allow the inline emphasis the explanations actually use, the list
// tags the LLM inconsistency vector emits (skipper builds one <li> per finding), plus the
// question-citation link below; every other tag collapses to a span and every event-handler
// attribute is dropped.
//
// `a` is allowed for the question citation, but `href` deliberately is NOT: the link is driven
// entirely by its `rel` (the question id), so withholding `href` keeps an injected destination out
// of a surface that AYTM staff read.
const EXPLANATION_TAGS = ['b', 'strong', 'i', 'em', 'u', 'a', 'ul', 'ol', 'li']
const EXPLANATION_ATTRS = ['rel']
const explanationHtml = value =>
  sanitizeHTML(value || '', { allowedTags: EXPLANATION_TAGS, allowedAttributes: EXPLANATION_ATTRS })

// The citation is an anchor inside the explanation's raw markup, so React never owns it — the click
// is caught by delegation on the container. Following it leaves the quality tab (§ the handler in
// the host shim), so it is routed up rather than handled here.
const QUESTION_LINK = '.redirect-to-question-link'

const Reject = ({ uid, processing, onReject }) => {
  const [rejectReason, setRejectReason] = useState(null)
  const [rejectOtherReason, setRejectOtherReason] = useState('')

  const dropdownRef = useRef(null)

  const containerExpanderRef = useRef()
  // Disposer returned by expand(); calling it unexpands the shared container.
  const expandDisposerRef = useRef(null)

  const onCancelReject = useCallback(() => {
    setRejectReason(null)
    setRejectOtherReason('')
  }, [setRejectReason, setRejectOtherReason])

  const onConfirmReject = useCallback(
    () => onReject(rejectOtherReason || rejectReason),
    [rejectReason, rejectOtherReason, onReject]
  )

  const onOtherReasonKeyDown = useCallback(
    event => {
      if (processing) return

      if (27 == event.keyCode) {
        onCancelReject()
      }
      if (13 == event.keyCode) {
        if (!rejectOtherReason.trim()) return
        onConfirmReject()
      }
    },
    [rejectOtherReason, processing, onCancelReject, onConfirmReject]
  )

  const onShowDropdown = () => {
    const overlay = dropdownRef.current.getOverlay()
    // Keep the disposer so an unmount while the overlay is open still unexpands the container.
    expandDisposerRef.current = containerExpanderRef.current?.expand?.(overlay)
  }

  const onHideDropdown = () => {
    expandDisposerRef.current?.()
    expandDisposerRef.current = null
  }

  // Mount once: resolve the container, build the expander, and register proper cleanup.
  useEffect(() => {
    const container = document.querySelector('#data_quality_content')
    if (!container) return

    containerExpanderRef.current = containerExpander(container, { margin: 0 })

    return () => {
      // Unexpand a still-open overlay so the shared container's min-height is reverted
      // (containerExpander exposes no destroy()).
      expandDisposerRef.current?.()
      expandDisposerRef.current = null
      containerExpanderRef.current = null
    }
  }, [])

  return 'other' == rejectReason ? (
    <>
      <span data-testid={`stat-dc-reject-other-cancel-${uid}`} onClick={processing ? null : onCancelReject}>
        <i className="icon-aytmfd-x-v2" />
      </span>
      <InputText
        data-testid={`stat-dc-reject-other-reason-${uid}`}
        className="p-composite p-inputtext-sm p-inputtext-rounded"
        placeholder="Please type a reason for rejection..."
        value={rejectOtherReason}
        disabled={processing}
        style={{ width: '250px' }}
        onKeyDown={processing ? null : onOtherReasonKeyDown}
        onChange={processing ? null : event => setRejectOtherReason(event.target.value)}
      />
      <Button
        data-testid={`stat-dc-reject-other-submit-${uid}`}
        className="p-button-sm p-button-rounded p-button-blue"
        label="Submit"
        loading={processing}
        disabled={processing || !rejectOtherReason.trim()}
        onClick={processing || !rejectOtherReason.trim() ? null : onConfirmReject}
      />
    </>
  ) : rejectReason ? (
    <>
      <Button
        data-testid={`stat-dc-reject-cancel-${uid}`}
        className="p-button-sm p-button-rounded p-button-blue p-button-secondary"
        label="Cancel"
        disabled={processing}
        onClick={processing ? null : onCancelReject}
      />
      <Button
        data-testid={`stat-dc-reject-submit-${uid}`}
        className="p-button-sm p-button-rounded p-button-blue"
        label="Submit"
        loading={processing}
        disabled={processing}
        onClick={processing ? null : onConfirmReject}
      />
    </>
  ) : (
    <>
      <Dropdown
        ref={dropdownRef}
        data-testid={`stat-dc-reject-reason-${uid}`}
        className="p-dropdown-sm p-dropdown-rounded p-dropdown-right p-dropdown-no-scroll p-dropdown-blue"
        placeholder="Reject"
        options={REJECTED_OPTIONS}
        optionValue="value"
        optionLabel="text"
        valueTemplate={() => 'Reject'}
        disabled={processing}
        onChange={processing ? null : event => setRejectReason(event.value)}
        onShow={processing ? null : onShowDropdown}
        onHide={processing ? null : onHideDropdown}
      />
    </>
  )
}

Reject.propTypes = {
  uid: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  processing: PropTypes.bool,
  onReject: PropTypes.func
}

Reject.displayName = 'Reject'

const Response = ({ uid, tier, item, vectors, review, onRestore, onReject, onScrollToQuestion }) => {
  const disabled = 'dc-restoring' == item.status

  // Delegated: the citation anchor lives in the explanation's raw markup, so React cannot bind to it
  // directly. The id is carried on the anchor's `rel`, whose leading segment is the question id.
  const onExplanationClick = event => {
    const link = event.target.closest(QUESTION_LINK)
    if (!link) return

    event.preventDefault()
    const rel = link.getAttribute('rel')
    if (rel) {
      const [id, batch] = rel.split('_')
      onScrollToQuestion(id, batch)
    }
  }

  const [error, setError] = useState(null)
  const [confirm, setConfirm] = useState(false)
  const [collapsed, setCollapsed] = useState(true)
  const [copied, setCopied] = useState(false)
  const [processing, setProcessing] = useState(['dc-rejecting', 'dc-restoring'].includes(item.status))

  const componentRef = useRef(null)
  const copiedTimer = useRef(null)

  // Tracks whether the component is still mounted — async restore/reject
  // callbacks can resolve after unmount (user scrolls away mid-request) and
  // must not touch state on an unmounted component.
  const mountedRef = useRef(true)

  // Every explanation the engine wrote for this response gets a row, so the header counts them
  // rather than the hit-vector map -- the two can disagree and the count labels what is rendered.
  const total = useMemo(() => Object.keys(item.explanations || {}).length, [item.explanations])

  const quarantined = useMemo(() => ['dc-auto-rejected', 'dc-restoring'].includes(item.status), [item.status])

  const onError = useCallback((error = null) => {
    if (!mountedRef.current) return
    setError(error)
  }, [])

  const onToggle = useCallback(() => setCollapsed(!collapsed), [collapsed])

  const onCopyUid = useCallback(
    event => {
      stopEvent(event)
      copyToClipboard(String(uid))
      setCopied(true)
      clearTimeout(copiedTimer.current)
      copiedTimer.current = setTimeout(() => mountedRef.current && setCopied(false), 2000)
    },
    [uid]
  )

  const onRestoreClick = useCallback(() => {
    setProcessing(true)
    onRestore(uid, error => {
      if (!mountedRef.current) return
      if (error) onError(error)
      setProcessing(false)
    })
  }, [uid, onError, onRestore])

  const onRejectClick = useCallback(
    reason => {
      setProcessing(true)
      onReject(uid, reason, error => {
        if (!mountedRef.current) return
        if (error) onError(error)
        setProcessing(false)
      })
    },
    [uid, onError, onReject]
  )

  // Parent (Responses.jsx) owns the `reviewResponse` listener and passes
  // down `review = { uid, tick }`. The effect watches `review.tick` — a
  // monotonic counter bumped on every `reviewResponse` dispatch — so the
  // scroll fires once per dispatch even when the user reviews the same UID
  // twice in a row. Depending on `tick` (not `uid`) avoids the previous
  // fire-once-via-state reset pattern.
  useEffect(() => {
    // Compare as strings: a uid is a number (nested row) or a sanitized string (flat stat-uid row),
    // so normalize both sides rather than relying on the two score sources staying same-typed.
    if (!review || String(review.uid) !== String(uid)) return
    setCollapsed(false)
    // Optional-chain the ref access: during a concurrent unmount or a narrow
    // render-before-attach window the ref can be null.
    componentRef.current?.scrollIntoView({ block: 'start' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [review?.tick])

  useEffect(() => {
    onError()
    setProcessing(['dc-rejecting', 'dc-restoring'].includes(item.status))
  }, [item.status, onError])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      clearTimeout(copiedTimer.current)
    }
  }, [])

  return (
    <div data-testid={`stat-dc-response-${uid}`} ref={componentRef} className={style.response}>
      <div data-testid={`stat-dc-response-header-${uid}`} className={style['response-header']} onClick={onToggle}>
        <div>
          <span
            data-testid={`stat-dc-response-uid-${uid}`}
            className={style['response-header-uid']}
            onClick={stopEvent}
          >
            {can(tier, CAPABILITY.RELEASE_QUARANTINE) ? uid : 'Response'}
          </span>
          {can(tier, CAPABILITY.RELEASE_QUARANTINE) ? (
            <span
              data-testid={`stat-dc-response-uid-copy-${uid}`}
              className={style['response-header-uid-copy']}
              title={copied ? 'Copied' : 'Copy response ID'}
              onClick={onCopyUid}
            >
              <i className={copied ? 'icon-aytmfd-checkmark' : 'icon-aytmfd-copy'} />
            </span>
          ) : null}
        </div>
        <div>
          <b data-testid={`stat-dc-response-vector-count-${uid}`} className={style['response-header-vectors']}>
            {total} {pluralize(total, 'vector', 'vectors')}
          </b>
        </div>
      </div>
      <TransitionPanel in={!collapsed}>
        {status => (
          <div className={style['response-content']}>
            {status === 'exited' ? null : (
              <>
                <div className={`${style['response-vector']} ${style['response-vector-header']}`}>
                  <div className={style['response-vector-header-title']}>Vector</div>
                  <div className={style['response-vector-header-details']}>Details</div>
                  <div className={`${style['response-vector-header-controls']} p-right`}>
                    {null !== error ? (
                      <Popup
                        className={`${style['component-popup']} rfd-ui-popup-simple`}
                        width="250px"
                        trigger="none"
                        direction="topleft"
                        show
                        content={() => (
                          <>
                            <PopupCloseButton className={style['component-popup-close']} onClose={onError}>
                              <i data-testid={`stat-dc-response-error-close-${uid}`} className="icon-aytmfd-x-v2" />
                            </PopupCloseButton>
                            <span
                              data-testid={`stat-dc-response-error-message-${uid}`}
                              dangerouslySetInnerHTML={{ __html: error.message }}
                            />
                          </>
                        )}
                      >
                        <div className={`${style['component-popup-anchor']} rfd-ui-popup-target-anchor`} />
                      </Popup>
                    ) : null}
                    {
                      // Releasing from quarantine is an admin-tier capability.
                      can(tier, CAPABILITY.RELEASE_QUARANTINE) ? (
                        quarantined ? (
                          confirm ? (
                            <>
                              <Button
                                data-testid={`stat-dc-response-restore-cancel-${uid}`}
                                className="p-button-sm p-button-rounded p-button-secondary"
                                label="Cancel"
                                disabled={processing || disabled}
                                onClick={processing || disabled ? null : () => setConfirm(false)}
                              />
                              <Button
                                data-testid={`stat-dc-response-restore-submit-${uid}`}
                                className="p-button-sm p-button-rounded"
                                label="Submit"
                                loading={processing}
                                disabled={processing || disabled}
                                onClick={processing || disabled ? null : onRestoreClick}
                              />
                            </>
                          ) : (
                            <Button
                              data-testid={`stat-dc-response-restore-${uid}`}
                              className="p-button-sm p-button-rounded"
                              label="Release from quarantine"
                              loading={processing}
                              disabled={processing || disabled}
                              onClick={processing || disabled ? null : () => setConfirm(true)}
                            />
                          )
                        ) : // `completed` and `excessive` fall through here to render Reject; the listed statuses do not.
                        ['open-ended-admin-rejected', 'open-ended-rejected', 'dc-rejected'].includes(
                            item.status
                          ) ? null : (
                          <Reject uid={uid} processing={processing} onReject={onRejectClick} />
                        )
                      ) : null
                    }
                  </div>
                </div>
                {Object.keys(item.explanations || {}).map(key => (
                  <div
                    key={`${uid}-${key}`}
                    data-testid={`stat-dc-response-vector-${uid}-${key}`}
                    className={style['response-vector']}
                  >
                    <div className={style['response-vector-icon']}>
                      {vectors.icons[key] ? (
                        <svg width="100" height="100" viewBox="0 0 100 100">
                          <path d={vectors.icons[key]} fill={vectors.colors[key]} />
                        </svg>
                      ) : null}
                    </div>
                    <div
                      data-testid={`stat-dc-response-vector-title-${uid}-${key}`}
                      className={style['response-vector-title']}
                    >
                      {/* an explanation the taxonomy does not place keys to its own id */}
                      {vectors.titles[key] || key}
                    </div>
                    <div
                      data-testid={`stat-dc-response-vector-details-${uid}-${key}`}
                      className={style['response-vector-details']}
                      onClick={onExplanationClick}
                      dangerouslySetInnerHTML={{ __html: explanationHtml(item.explanations[key]) }}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </TransitionPanel>
    </div>
  )
}

Response.propTypes = {
  // A nested-shape row yields a numeric uid; a flat stat-uid row yields a sanitized string id.
  uid: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  tier: PropTypes.string,
  item: PropTypes.object,
  vectors: PropTypes.object,
  review: PropTypes.shape({
    uid: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    tick: PropTypes.number
  }),
  onRestore: PropTypes.func,
  onReject: PropTypes.func,
  onScrollToQuestion: PropTypes.func
}

Response.displayName = 'Response'

export default Response
